import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { User, UserManager } from 'oidc-client';
import { BehaviorSubject, interval, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, finalize, switchMap, tap } from 'rxjs/operators';
import { UserInfo } from 'src/app/api/models';
import { UserService } from 'src/app/api/services';
import { AppConfig } from 'src/app/config/app-config';
import { AppConfigService } from 'src/app/config/app-config.service';
import { FuldmagtChoiceService } from 'src/app/projekter/fuldmagt-choice.service';
import { LocalStorageService } from '../localstorage/localstorage.service';
import { UserManagerAccessorService } from '../oidc/user-manager-accessor.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user$: Observable<User> = of();
  userInfo$ = new BehaviorSubject<UserInfo>(null);
  isCitizen$ = new BehaviorSubject<boolean>(null);

  get user(): User {
    return this._user;
  }

  set user(user: User) {
    if (user == null) {
      this._user = null;
    } else {
      this._user = user;

      if (!this.userInfo$.value && !this._userInfoIsLoading) {
        this.getUserInfo().subscribe();
      }
    }
  }

  get callbackUrl(): string {
    return this.localStorageService.select<string>('callbackUrl').value;
  }

  set callbackUrl(url: string) {
    if (url.endsWith('loggetud')) {
      url = ''; // Don't navigate back to "LoggedOut" page when logging in from that page
    }
    this.localStorageService.set('callbackUrl', url);
  }

  private _appConfig: AppConfig;
  private _user: User;
  private _manager: UserManager;
  private _userInfoIsLoading = false;
  private _user$ = new BehaviorSubject<User>(null);

  constructor(
    private usermanager: UserManagerAccessorService,
    private router: Router,
    private userService: UserService,
    private localStorageService: LocalStorageService,
    private appConfigService: AppConfigService,
    private fuldmagtChoiceService: FuldmagtChoiceService
  ) {}

  initialize() {
    this._appConfig = this.appConfigService.getConfig();

    this._manager = this.usermanager.getManager();
    this._manager.getUser().then(user => {
      this.user = user;
    });
    this.addAccessTokenExpiredEvent();
    this.addUserSignedOutEvent();
    this.addSilentRenewError();
    this.addUserLoaded();
    this.addUserUnloaded();

    this.userInfo$.pipe(filter(userInfo => userInfo != null)).subscribe(userInfo => {
      this.isCitizen$.next(!userInfo.isEmployee);

      const fuldmagtChoice = this.fuldmagtChoiceService.getChoice();
      if (userInfo.hasFuldmagter && !fuldmagtChoice && !this.router.url.includes('skiftbruger')) {
        this.router.navigate(['/skiftbruger'], {
          queryParams: {
            returnUrl: this.callbackUrl,
          },
        });
      }
      this.localStorageService.remove('callbackUrl');
    });

    // Update local user regularly from oidc storage
    interval(this._appConfig.checkSessionInterval).subscribe(() => {
      this._manager.getUser().then(user => {
        this._user$.next(user);
      });
    });

    // Only emit user when its not the same as the last emitted user.
    this.user$ = this._user$.pipe(
      distinctUntilChanged((user1, user2) => {
        if (!user1 || !user2) {
          if (!user1 && !user2) {
            return true;
          }

          return false;
        }

        return user1.access_token === user2.access_token;
      })
    );

    // When user updates, keep local user object in sync and reset tokenIsAboutToExpire flag
    this.user$.subscribe(user => {
      this.user = user;
    });
  }

  /**
   * Signs user in on the IdentityServer and sets the user property
   * @param callbackUrl is saved in localstorage. Used in postSignInRedirect method for redirecting users to the correct page
   * @param force if set to false or omitted, the user will not be redirected if we already have a user object
   */
  login(callbackUrl: string, force = false): void {
    this._manager.getUser().then(user => {
      if (user && !force) {
        this._user$.next(user);
        this.router.navigateByUrl(callbackUrl);
      } else {
        this.callbackUrl = callbackUrl;
        this._manager
          .signinRedirect()
          .then(() => this._manager.getUser())
          .then(usr => this._user$.next(usr))
          .then(() => this.fuldmagtChoiceService.remove());
      }
    });
  }

  /**
   * Logs the user in silently. Meaning, if the user has a session
   * in identityserver, the user is automatically logged in and their
   * token is renewed, but will not redirect user to login page
   */
  async loginSilent(): Promise<void> {
    /* OBS: This causes "Showing error: prompt=none was requested but user is not authenticated" to be logged as information.
     * This happens even though loginSilent is being called by design:
     * https://github.com/IdentityServer/IdentityServer3/issues/1821#issuecomment-139212426
     */
    return this._manager
      .signinSilent()
      .then(user => {
        this._manager.storeUser(user);
      })
      .catch(() => {
        // Work around to handle Iframe window timeout errors on browsers
        this._manager.getUser().then((user: User) => {
          this._manager.storeUser(user);
        });
      });
  }

  /**
   * Call the signoutRedirect on the manager property.
   * The signoutRedirect signs the user out on the IdentityServer
   */
  logout(triggerExternalSignout = true): void {
    this._manager
      .signoutRedirect({
        extraQueryParams: {
          triggerExternalSignout,
        },
      })
      .then(() => {
        this._user$.next(null);
        this._manager.clearStaleState();
        this.fuldmagtChoiceService.remove();
      });
  }

  /**
   * This method is used in the callback component to redirect the user to the correct page after signing in on IdentityServer
   */
  postSignInRedirect(): void {
    this._manager.signinRedirectCallback().then(user => {
      this._user$.next(user);
      this.router.navigateByUrl(this.callbackUrl);
    });
  }

  getUserInfo(): Observable<UserInfo> {
    this._userInfoIsLoading = true;

    return this.userService
      .clearCache()
      .pipe(filter(() => !!this.user))
      .pipe(
        switchMap(() =>
          this.userService.getUserInfo().pipe(
            tap(userInfo => this.userInfo$.next(userInfo)),
            finalize(() => (this._userInfoIsLoading = false))
          )
        )
      );
  }

  /**
   * Initiates event that fires when token is expired
   */
  private addAccessTokenExpiredEvent(): void {
    this._manager.events.addAccessTokenExpired(() => {
      this.logout(false);
    });
  }

  private addUserSignedOutEvent(): void {
    this._manager.events.addUserSignedOut(() => {
      this.logout();
    });
  }

  private addUserLoaded(): void {
    this._manager.events.addUserLoaded(user => {
      this._user$.next(user);
    });
  }

  private addUserUnloaded(): void {
    this._manager.events.addUserUnloaded(() => {
      this._user$.next(null);
    });
  }

  private addSilentRenewError() {
    this._manager.events.addSilentRenewError(error => {
      console.error('silentRenewError', error);
    });
  }
}
